import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Loading,
  PageHeader,
  Select,
  Textarea,
  Toast,
} from '@/components/ui';

interface EngineConfig {
  company?: {
    name?: string;
    greeting?: string;
    closing?: string;
    ai_disclosure?: string;
    tone?: string;
    language?: string;
  };
  prompt?: { persona?: string | null; system_extra?: string | null; rules?: string[] };
  qualification?: {
    required_fields?: string[];
    optional_fields?: string[];
    min_score?: number;
    max_questions?: number;
    ask_order?: string[];
  };
  transfer?: {
    mode?: string;
    timeout_sec?: number;
    fallback?: string[];
    max_attempts?: number;
    // BLIND or WARM: the SHAPE of the hand-over rather than its destination.
    handoff?: string;
  };
  compliance?: Record<string, boolean | number>;
  amd?: {
    enabled?: boolean;
    beep_detection?: boolean;
    cadence_detection?: boolean;
    sensitivity?: string;
  };
}

interface Engine {
  id: string;
  key: string;
  name: string;
  engine: string;
  vertical: string;
  summary: string | null;
  description: string | null;
  sort_order: number;
  active: boolean;
  is_premade: boolean;
  default_config: EngineConfig & {
    soundboard?: {
      max_retries?: number;
      transfer_phrases?: string[];
      reject_phrases?: string[];
    };
  };
}

/**
 * A Soundboard Bot is not an AI Bot with the model switched off.
 *
 * It has no prompt, no persona, no rules and no fields to collect, so the
 * panel below rendered seven "Not set" rows for it — which reads as a broken
 * engine somebody forgot to finish, and is the first thing anybody asks
 * about. What it actually has is recordings and a phrase table, and both live
 * on the Soundboards page.
 */
/** A textarea's line separator, named because writing it inline keeps
 *  getting mangled by the tooling that edits this file. */
const NEWLINE = String.fromCharCode(10);

function isSoundboard(engine: Engine): boolean {
  return engine.engine === 'soundboard' || engine.key === 'soundboard';
}

/**
 * The premade bot engines, with their prompts shown in full.
 *
 * The whole conversation design is visible here rather than buried in a JSON
 * blob: an operator picking an engine for a client needs to know what it will
 * actually say, and the compliance lines in particular are worth being able to
 * read before a call is made rather than after one goes wrong.
 */
export default function Templates() {
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; tone: 'good' | 'bad' } | null>(
    null,
  );
  const { can } = useAuth();

  const query = useQuery({
    queryKey: ['admin-bot-templates'],
    queryFn: () => api.get<{ data: Engine[] }>('/admin/bot-templates'),
  });

  if (query.isPending) return <Loading />;
  if (query.error) return <ErrorState error={query.error} retry={() => query.refetch()} />;

  const engines = [...query.data.data].sort((a, b) => a.sort_order - b.sort_order);

  return (
    <div className="w-full">
      <PageHeader
        title="Bot engines"
        description="A complete conversation design per vertical. A client picks one and overrides only their company name."
      />

      {engines.length === 0 ? (
        <Card>
          <EmptyState
            title="No engines installed"
            description="Run the seed to install the premade engines."
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {engines.map((engine) => {
            const open = openKey === engine.key;
            const config = engine.default_config ?? {};
            const company = config.company ?? {};
            const qualification = config.qualification ?? {};

            return (
              <Card key={engine.id} padded={false}>
                <button
                  type="button"
                  onClick={() => setOpenKey(open ? null : engine.key)}
                  aria-expanded={open}
                  className="flex w-full items-start justify-between gap-4 p-4 text-left transition-colors hover:bg-sunken"
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-ink-900 dark:text-ink-50">
                        {engine.name}
                      </span>
                      <code className="rounded bg-sunken px-1.5 py-0.5 font-mono text-2xs text-ink-600 dark:text-ink-300">
                        {engine.key}
                      </code>
                      {!engine.active && <Badge tone="gray">Inactive</Badge>}
                      {engine.is_premade && <Badge tone="blue">Premade</Badge>}
                    </div>
                    {engine.summary && (
                      <p className="mt-1.5 max-w-2xl text-xs leading-relaxed text-ink-600 dark:text-ink-300">
                        {engine.summary}
                      </p>
                    )}
                  </div>
                  <span className="shrink-0 pt-1 text-xs font-medium text-brand-600 dark:text-brand-400">
                    {open ? 'Hide prompt' : 'Show prompt'}
                  </span>
                </button>

                {open && isSoundboard(engine) && (
                  <SoundboardEngine engine={engine} onToast={setToast} />
                )}

                {open && editingKey === engine.key && !isSoundboard(engine) && (
                  <EngineEditor
                    engine={engine}
                    onClose={() => setEditingKey(null)}
                    onToast={setToast}
                  />
                )}

                {open && editingKey !== engine.key && !isSoundboard(engine) && (
                  <div className="animate-fade-in space-y-5 border-t border-line p-4">
                    {can('bot_templates:update') && (
                      <div className="flex justify-end">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => setEditingKey(engine.key)}
                        >
                          Edit this engine
                        </Button>
                      </div>
                    )}
                    {/* Greeting first: it is the only line every single call
                        begins with, and the only one that names the client. */}
                    <Section label="Opening line">
                      <Quote>
                        {company.greeting?.replace('{{company}}', '⟨company name⟩') ??
                          'Not set'}
                      </Quote>
                      <p className="mt-1.5 text-2xs text-ink-500 dark:text-ink-400">
                        ⟨company name⟩ is replaced with each client&apos;s own name, set
                        on their profile. Nothing else changes per client.
                      </p>
                    </Section>

                    <Section label="If asked whether this is a real person">
                      <Quote>{company.ai_disclosure ?? 'Not set'}</Quote>
                      <p className="mt-1.5 text-2xs text-ink-500 dark:text-ink-400">
                        Answered honestly and immediately, on every engine. This is
                        not configurable per client.
                      </p>
                    </Section>

                    <Section label="Closing line">
                      <Quote>{company.closing ?? 'Not set'}</Quote>
                    </Section>

                    {config.prompt?.rules?.length ? (
                      <Section label="Rules the model is held to">
                        <ul className="space-y-1.5">
                          {config.prompt.rules.map((rule) => (
                            <li
                              key={rule}
                              className="flex gap-2 text-xs leading-relaxed text-ink-700 dark:text-ink-200"
                            >
                              <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-ink-400" />
                              {rule}
                            </li>
                          ))}
                        </ul>
                      </Section>
                    ) : null}

                    <div className="grid gap-5 sm:grid-cols-2">
                      <Section label="Must collect to qualify">
                        <FieldList items={qualification.required_fields} />
                        <p className="mt-1.5 text-2xs text-ink-500 dark:text-ink-400">
                          Minimum score {qualification.min_score ?? '—'}, at most{' '}
                          {qualification.max_questions ?? '—'} questions.
                        </p>
                      </Section>
                      <Section label="Collects if offered">
                        <FieldList items={qualification.optional_fields} />
                      </Section>
                    </div>

                    {qualification.ask_order?.length ? (
                      <Section label="Question order">
                        <div className="scroll-x flex items-center gap-1.5 pb-1">
                          {qualification.ask_order.map((field, index) => (
                            <span key={field} className="flex shrink-0 items-center gap-1.5">
                              {index > 0 && <span className="text-ink-300">→</span>}
                              <code className="rounded bg-sunken px-1.5 py-0.5 font-mono text-2xs text-ink-700 dark:text-ink-200">
                                {field}
                              </code>
                            </span>
                          ))}
                        </div>
                      </Section>
                    ) : null}

                    <Section label="Handing over to a human">
                      <p className="text-xs text-ink-700 dark:text-ink-200">
                        {config.transfer?.mode ?? '—'}, {config.transfer?.timeout_sec ?? '—'}s
                        timeout, up to {config.transfer?.max_attempts ?? '—'} attempt(s).
                      </p>
                      {config.transfer?.fallback?.length ? (
                        <p className="mt-1 text-2xs text-ink-500 dark:text-ink-400">
                          If that fails: {config.transfer.fallback.join(' → ')}
                        </p>
                      ) : null}
                    </Section>

                    <Section label="Compliance">
                      <div className="flex flex-wrap gap-1.5">
                        {Object.entries(config.compliance ?? {}).map(([key, value]) => (
                          <Badge
                            key={key}
                            tone={value === true ? 'green' : value === false ? 'gray' : 'blue'}
                          >
                            {key.replace(/_/g, ' ')}
                            {typeof value === 'number' ? `: ${value}` : ''}
                          </Badge>
                        ))}
                      </div>
                      <p className="mt-2 text-2xs leading-relaxed text-ink-500 dark:text-ink-400">
                        These shape the conversation, but they are not what keeps
                        it compliant. Do-not-call and refusal detection run as
                        deterministic phrase rules <em>before</em> the model on
                        every turn, and a terminal intent they raise cannot be
                        argued away by anything in this prompt.
                      </p>
                    </Section>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {toast && (
        <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />
      )}
    </div>
  );
}

/**
 * What a soundboard engine actually is.
 *
 * The AI panel asked it seven questions it has no answers to and printed
 * "Not set" seven times, which reads as an engine somebody forgot to finish.
 * Nothing was missing: a Soundboard Bot has no prompt, because it never calls
 * a language model and never synthesises a word. It plays recordings and
 * matches what it hears against a phrase table.
 */
function BuiltInList({
  label,
  words,
  note,
}: {
  label: string;
  words: string[];
  note?: string;
}) {
  return (
    <div>
      <div className="mb-1 text-xs font-medium">
        {label} <span className="text-ink-500">({words.length})</span>
      </div>
      {note && <p className="mb-1 text-[11px] leading-snug text-ink-500">{note}</p>}
      {/* Scrolled rather than truncated: a list that cuts off at ten is a list
          an operator cannot use to answer "is this phrase covered", which is
          the only question they open it to ask. */}
      <div className="max-h-40 overflow-y-auto rounded border border-line bg-sunken p-2 font-mono text-[11px] leading-relaxed">
        {words.map((word) => (
          <div key={word}>{word}</div>
        ))}
      </div>
    </div>
  );
}

function SoundboardEngine({
  engine,
  onToast,
}: {
  engine: Engine;
  onToast: (t: { message: string; tone: 'good' | 'bad' }) => void;
}) {
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const config = engine.default_config?.soundboard ?? {};
  const [retries, setRetries] = useState(String(config.max_retries ?? 0));
  const [transfer, setTransfer] = useState(
    (config.transfer_phrases ?? []).join(NEWLINE),
  );
  const [reject, setReject] = useState((config.reject_phrases ?? []).join(NEWLINE));
  const amd = engine.default_config?.amd ?? {};
  const [amdOn, setAmdOn] = useState(Boolean(amd.enabled));
  const [sensitivity, setSensitivity] = useState(amd.sensitivity ?? 'balanced');
  const [showBuiltIn, setShowBuiltIn] = useState(false);
  const transferCfg = engine.default_config?.transfer ?? {};
  const [handoff, setHandoff] = useState(transferCfg.handoff ?? 'BLIND');

  // THE BOXES ARE EMPTY AND THE LIST WAS INVISIBLE.
  //
  // An empty box means "use the built-in homeowner list", which the card has
  // always said — and nothing anywhere showed what that list contained. So
  // the one control that decides who reaches a paid closer could not be read:
  // an operator could not check whether "we bought it last year" counted, or
  // whether "my landlord" was covered.
  //
  // Fetched rather than duplicated here. A copy in the frontend is the copy
  // that drifts, and it would drift towards showing rules the engine is not
  // applying.
  const builtIn = useQuery<{
    data: {
      transfer_phrases: string[];
      reject_phrases: string[];
      reject_words: string[];
      short_yes: string[];
      short_no: string[];
    };
  }>({
    queryKey: ['soundboard-default-phrases'],
    queryFn: () => api.get('/admin/soundboards/default-phrases'),
  });
  const tables = builtIn.data?.data;

  const lines = (value: string) =>
    value
      .split(NEWLINE)
      .map((line) => line.trim())
      .filter(Boolean);

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/admin/bot-templates/${engine.id}`, {
        // Merged over the engine's existing config: it holds compliance and
        // language keys this form has no boxes for, and writing back only
        // what is on screen would delete them for every board at once.
        default_config: {
          ...engine.default_config,
          soundboard: {
            ...config,
            max_retries: Math.max(0, Math.min(4, Number(retries) || 0)),
            transfer_phrases: lines(transfer),
            reject_phrases: lines(reject),
          },
          // Answering-machine detection is NOT a soundboard setting — it is
          // the same block the AI engine reads, and this is simply the only
          // screen a soundboard operator ever opens. Merged the same way, so
          // the beep and cadence switches this form does not show survive.
          amd: { ...amd, enabled: amdOn, sensitivity },
          // Merged the same way, so the DID, in-group, timeout and whisper
          // settings this form has no boxes for survive.
          transfer: { ...transferCfg, handoff },
        },
      }),
    onSuccess: () => {
      onToast({
        message: 'Saved. Every soundboard uses it from their next call.',
        tone: 'good',
      });
      queryClient.invalidateQueries({ queryKey: ['admin-bot-templates'] });
    },
    onError: (err) => onToast({ message: errorMessage(err, 'Could not save'), tone: 'bad' }),
  });

  const editable = can('bot_templates:update');

  return (
    <div className="animate-fade-in space-y-4 border-t border-line p-4">
      <div className="rounded-lg bg-sunken px-3 py-2 text-xs leading-relaxed text-ink-700 dark:text-ink-200">
        This engine has no prompt, and that is not something missing from it.
        A Soundboard Bot never calls a language model and never synthesises a
        word — it plays recordings an operator made, listens to the answer,
        and hands over the people who said the thing it is screening for.
        Speech recognition is its only per-call cost.
      </div>

      <Section label="What it says">
        <p className="text-xs leading-relaxed text-ink-700 dark:text-ink-200">
          Seven recordings, per board: a greeting, the qualifying question,
          rebuttals for an unclear answer, a repeat, what to say on the way to
          a human, and two goodbyes. They belong to the board, not to this
          engine, because two boards on the same engine screen for different
          things in different voices. Upload or generate them on{' '}
          <strong>Soundboard Bots</strong>.
        </p>
      </Section>

      <Section label="How it decides">
        <p className="text-xs leading-relaxed text-ink-700 dark:text-ink-200">
          A rejection wins wherever it appears — “yes, but we’re renting” is a
          renter — then the transfer phrases, then short answers to the
          question that was just asked. Anything matching neither is asked
          again up to the retry limit below, and then the call ends{' '}
          <strong>without</strong> a transfer. An unclear answer is never
          treated as a soft yes.
        </p>
      </Section>

      {/* EVERY BOARD, FROM ONE PLACE. `bots.config` is deep-merged over this,
          so a value here reaches every soundboard that has not deliberately
          overridden it — which is what was asked for, and what the per-board
          screens could not do. */}
      <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
        <strong>These apply to every Soundboard Bot.</strong> A board that has
        had the same setting changed on its own page keeps its own value; every
        other board takes what is here, on its next call.
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label="Times to ask again"
          hint="0 — play the clips once and stop, which is what a board of recordings wants. Higher replays the same recording at somebody who already heard it."
        >
          <Input
            type="number"
            min={0}
            max={4}
            value={retries}
            disabled={!editable}
            onChange={(e) => setRetries(e.target.value)}
          />
        </Field>
        <div />
        <Field
          label="Transfer when you hear"
          hint={
            transfer.trim()
              ? 'One per line. This board uses these and not the built-in list.'
              : 'Empty — using the built-in homeowner list below. Short answers such as yes and yeah always count.'
          }
        >
          <Textarea
            rows={8}
            className="font-mono text-xs"
            value={transfer}
            disabled={!editable}
            onChange={(e) => setTransfer(e.target.value)}
          />
          {editable && !transfer.trim() && tables && (
            <Button
              variant="ghost"
              className="mt-1.5 text-xs"
              onClick={() => setTransfer(tables.transfer_phrases.join(NEWLINE))}
            >
              Start from the built-in list ({tables.transfer_phrases.length})
            </Button>
          )}
        </Field>
        <Field
          label="Never transfer when you hear"
          hint={
            reject.trim()
              ? 'Checked first, and it wins outright. This board uses these and not the built-in list.'
              : 'Checked first, and it wins outright. Empty — using the built-in list below.'
          }
        >
          <Textarea
            rows={8}
            className="font-mono text-xs"
            value={reject}
            disabled={!editable}
            onChange={(e) => setReject(e.target.value)}
          />
          {editable && !reject.trim() && tables && (
            <Button
              variant="ghost"
              className="mt-1.5 text-xs"
              onClick={() => setReject(tables.reject_phrases.join(NEWLINE))}
            >
              Start from the built-in list ({tables.reject_phrases.length})
            </Button>
          )}
        </Field>
      </div>

      {/* WHAT "THE BUILT-IN LIST" IS.
          Shown rather than described. Until this existed the boxes were empty,
          the hint named a list nobody could see, and the two hints together
          were a trap: supplying either box REPLACES the built-in table, so one
          extra phrase typed in silently deleted thirty-three. */}
      <div className="rounded-lg border border-line">
        <button
          type="button"
          className="flex w-full items-center justify-between px-3 py-2 text-left text-xs font-medium"
          onClick={() => setShowBuiltIn((open) => !open)}
        >
          <span>
            Show the built-in list
            {tables
              ? ` — ${tables.transfer_phrases.length} transfer, ${tables.reject_phrases.length} rejection`
              : ''}
          </span>
          <span className="text-ink-500">{showBuiltIn ? '−' : '+'}</span>
        </button>

        {showBuiltIn && (
          <div className="space-y-3 border-t border-line px-3 py-3">
            <p className="text-xs leading-relaxed text-ink-700 dark:text-ink-200">
              These are what the engine applies when a box above is empty.
              Leaving a box empty also means it <strong>tracks</strong> this
              list — a board that has its own copy stops tracking it and will
              not pick up phrases added in a later release.
            </p>
            {builtIn.isError && (
              <p className="text-xs text-rose-600">Could not load the list.</p>
            )}
            {tables && (
              <div className="grid gap-3 sm:grid-cols-2">
                <BuiltInList label="Transfer" words={tables.transfer_phrases} />
                <BuiltInList label="Never transfer" words={tables.reject_phrases} />
                <BuiltInList
                  label="Rejected on the word alone"
                  words={tables.reject_words}
                  note="Anywhere in the answer, not just as a phrase."
                />
                <BuiltInList
                  label="Short answers that count as yes"
                  words={tables.short_yes}
                  note="Only to a question just asked. Not editable — they are the same in every vertical."
                />
              </div>
            )}
          </div>
        )}
      </div>

      <Section label="Handing over to a human">
        <p className="text-xs leading-relaxed text-ink-700 dark:text-ink-200">
          <strong>Blind</strong> hands the channel to the Transfer extension on
          the Vicidial server and Glee leaves the call entirely — the agent
          extension is free for the next call the moment the closer is dialled.
          <strong> Warm</strong> keeps Glee holding the customer&apos;s channel
          for the whole of the closer&apos;s conversation; the bot is off the
          media path but the channel is not, so the extension stays busy and
          the dialler queues behind it.
        </p>
        <p className="mt-1.5 text-xs leading-relaxed text-ink-700 dark:text-ink-200">
          Blind is the default here because a board has nothing left to say. An
          AI Bot has a reason to stay — it can take the call back if the
          transfer fails — and a recording cannot. It needs a{' '}
          <strong>Transfer extension</strong> set on the Vicidial server; without
          one the hand-over falls back to warm and says so in the log.
        </p>

        <div className="mt-3 max-w-xs">
          <Field label="Hand-over">
            <Select
              value={handoff}
              disabled={!editable}
              onChange={(e) => setHandoff(e.target.value)}
            >
              <option value="BLIND">Blind — Glee leaves the call</option>
              <option value="WARM">Warm — Glee stays on the channel</option>
            </Select>
          </Field>
        </div>
      </Section>

      <Section label="Answering machines">
        <p className="text-xs leading-relaxed text-ink-700 dark:text-ink-200">
          Detection from the <strong>transcript</strong> is always on and is
          not configurable — &ldquo;please leave a message after the tone&rdquo;
          ends the call on every engine, and it costs nothing because the
          words arrive anyway.
        </p>
        <p className="mt-1.5 text-xs leading-relaxed text-ink-700 dark:text-ink-200">
          This switch is the other one: detection from the <strong>audio</strong>,
          which hears a beep or four unbroken seconds of speech before a word
          is transcribed. It is off by default, and that is not timidity — a
          platform running beside a dialler whose own AMD is enabled does not
          need it, and two detectors both allowed to hang up is twice the
          chance of hanging up on somebody real. Turn Vicidial&apos;s off, or
          leave this off.
        </p>

        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={amdOn}
              disabled={!editable}
              onChange={(e) => setAmdOn(e.target.checked)}
            />
            Detect answering machines from the audio
          </label>
          {amdOn && (
            <Field
              label="Sensitivity"
              hint="Higher hangs up sooner and is likelier to hang up on a slow talker."
            >
              <select
                className="block w-full rounded-md border border-line bg-surface px-3 py-1.5 text-sm"
                value={sensitivity}
                disabled={!editable}
                onChange={(e) => setSensitivity(e.target.value)}
              >
                <option value="cautious">Cautious</option>
                <option value="balanced">Balanced</option>
                <option value="aggressive">Aggressive</option>
              </select>
            </Field>
          )}
        </div>
      </Section>

      {editable && (
        <Button loading={save.isPending} onClick={() => save.mutate()}>
          Save for every soundboard
        </Button>
      )}

      <Section label="Compliance">
        <div className="flex flex-wrap gap-1.5">
          {Object.entries(engine.default_config?.compliance ?? {}).map(([key, value]) => (
            <Badge key={key} tone={value === true ? 'green' : 'gray'}>
              {key.replace(/_/g, ' ')}
            </Badge>
          ))}
        </div>
        <p className="mt-2 text-2xs leading-relaxed text-ink-500 dark:text-ink-400">
          Do-not-call and refusal detection run as deterministic phrase rules
          on every turn, the same ones the AI engines run, and calling hours
          are enforced identically. A soundboard is cheaper to run and is not a
          cheaper kind of call.
        </p>
      </Section>
    </div>
  );
}

/**
 * Editing an engine.
 *
 * THIS IS THE ENGINE, NOT ONE BOT — which is the whole reason it is worth
 * having and the whole reason it is worth a warning. A bot's own config is
 * deep-merged over this at the start of every call, so a line changed here
 * changes it for every bot on this engine that has not overridden that field,
 * on their next call, with no deploy and no restart.
 *
 * That is exactly what somebody fixing a badly worded question wants, and
 * exactly what somebody who thinks they are editing one client's bot does
 * not. So the warning says which, in those words, above the buttons.
 *
 * Only the conversation design is editable. The transfer plumbing, the
 * compliance switches and the engine key are not: they are wired to code
 * elsewhere, and a text box that lets somebody type a mode nothing implements
 * is a way to break every bot on an engine at once.
 */
function EngineEditor({
  engine,
  onClose,
  onToast,
}: {
  engine: Engine;
  onClose: () => void;
  onToast: (t: { message: string; tone: 'good' | 'bad' }) => void;
}) {
  const queryClient = useQueryClient();
  const config = engine.default_config ?? {};
  const company = config.company ?? {};
  const qualification = config.qualification ?? {};

  const [greeting, setGreeting] = useState(company.greeting ?? '');
  const [disclosure, setDisclosure] = useState(company.ai_disclosure ?? '');
  const [closing, setClosing] = useState(company.closing ?? '');
  const [persona, setPersona] = useState(config.prompt?.persona ?? '');
  const [rules, setRules] = useState((config.prompt?.rules ?? []).join('\n'));
  const [required, setRequired] = useState(
    (qualification.required_fields ?? []).join('\n'),
  );
  const [optional, setOptional] = useState(
    (qualification.optional_fields ?? []).join('\n'),
  );
  const [minScore, setMinScore] = useState(String(qualification.min_score ?? ''));
  const [maxQuestions, setMaxQuestions] = useState(
    String(qualification.max_questions ?? ''),
  );

  const lines = (value: string) =>
    value
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/admin/bot-templates/${engine.id}`, {
        // Merged over the existing config rather than replacing it. An engine
        // holds keys this form does not show — voice settings, AMD, limits —
        // and writing back only the fields on screen would silently delete
        // every one of them for every bot on the engine.
        default_config: {
          ...config,
          company: {
            ...company,
            greeting: greeting.trim(),
            ai_disclosure: disclosure.trim(),
            closing: closing.trim(),
          },
          prompt: {
            ...(config.prompt ?? {}),
            persona: persona.trim() || null,
            rules: lines(rules),
          },
          qualification: {
            ...qualification,
            required_fields: lines(required),
            optional_fields: lines(optional),
            ...(minScore.trim() ? { min_score: Number(minScore) } : {}),
            ...(maxQuestions.trim() ? { max_questions: Number(maxQuestions) } : {}),
          },
        },
      }),
    onSuccess: () => {
      onToast({
        message: `${engine.name} saved. Every bot on this engine uses it from their next call.`,
        tone: 'good',
      });
      queryClient.invalidateQueries({ queryKey: ['admin-bot-templates'] });
      onClose();
    },
    onError: (err) => onToast({ message: errorMessage(err, 'Could not save'), tone: 'bad' }),
  });

  return (
    <div className="animate-fade-in space-y-4 border-t border-line p-4">
      <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
        <strong>This is the engine, not one bot.</strong> Every bot using{' '}
        <code>{engine.key}</code> that has not overridden a field takes the new
        wording on its next call — including bots that are live right now. To
        change one client only, edit that client&apos;s bot instead.
      </div>

      <Field
        label="Opening line"
        hint="Use ⟨company⟩ where the client's own name goes: type {{company}}."
      >
        <Textarea rows={2} value={greeting} onChange={(e) => setGreeting(e.target.value)} />
      </Field>

      <Field
        label="If asked whether this is a real person"
        hint="Answered honestly and immediately. Removing this does not stop the bot disclosing — the engine answers regardless — it only makes the wording worse."
      >
        <Textarea
          rows={2}
          value={disclosure}
          onChange={(e) => setDisclosure(e.target.value)}
        />
      </Field>

      <Field label="Closing line">
        <Textarea rows={2} value={closing} onChange={(e) => setClosing(e.target.value)} />
      </Field>

      <Field label="Persona" hint="Who the bot is. Left blank, the engine's default is used.">
        <Textarea rows={2} value={persona} onChange={(e) => setPersona(e.target.value)} />
      </Field>

      <Field
        label="Rules the model is held to"
        hint="One per line. These shape the conversation; they are not what keeps it compliant — do-not-call and refusal detection run before the model on every turn and cannot be argued away by anything typed here."
      >
        <Textarea
          rows={8}
          className="font-mono text-xs"
          value={rules}
          onChange={(e) => setRules(e.target.value)}
        />
      </Field>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Must collect to qualify" hint="One field name per line.">
          <Textarea
            rows={5}
            className="font-mono text-xs"
            value={required}
            onChange={(e) => setRequired(e.target.value)}
          />
        </Field>
        <Field label="Collects if offered" hint="One field name per line.">
          <Textarea
            rows={5}
            className="font-mono text-xs"
            value={optional}
            onChange={(e) => setOptional(e.target.value)}
          />
        </Field>
        <Field label="Minimum score to transfer">
          <Input
            type="number"
            value={minScore}
            onChange={(e) => setMinScore(e.target.value)}
          />
        </Field>
        <Field label="Most questions to ask">
          <Input
            type="number"
            value={maxQuestions}
            onChange={(e) => setMaxQuestions(e.target.value)}
          />
        </Field>
      </div>

      <div className="flex gap-2">
        <Button loading={save.isPending} onClick={() => save.mutate()}>
          Save engine
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-1.5 text-2xs font-semibold uppercase tracking-[0.06em] text-ink-500 dark:text-ink-400">
        {label}
      </h3>
      {children}
    </div>
  );
}

function Quote({ children }: { children: React.ReactNode }) {
  return (
    <p className="rounded-lg border-l-2 border-brand-400 bg-sunken px-3 py-2 text-xs italic leading-relaxed text-ink-800 dark:text-ink-100">
      {children}
    </p>
  );
}

function FieldList({ items }: { items?: string[] }) {
  if (!items?.length) {
    return <p className="text-xs text-ink-400">None</p>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((item) => (
        <code
          key={item}
          className="rounded bg-sunken px-1.5 py-0.5 font-mono text-2xs text-ink-700 dark:text-ink-200"
        >
          {item}
        </code>
      ))}
    </div>
  );
}
